import { UI_MODES, type AuthResponse, type UiMode } from '@batixa/domain';
import { useMutation } from '@tanstack/react-query';
import { BadgeCheck, Building2, CreditCard, History, Ruler, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote, Field } from '../components/ui';
import { api } from '../lib/api';
import { MODE_LABELS, PageHeader } from '../layout/AppShell';

const MODE_HINTS: Record<UiMode, string> = {
  SIMPLE: 'Devis rapide, clients et assistant : seuls les écrans essentiels sont affichés.',
  PRO: 'Tous les écrans du chiffrage : projets, métré détaillé, bibliothèque, rapports.',
  EXPERT: 'Comme le mode Professionnel, avec le laboratoire de formules ouvert par défaut.',
};

function ModeCard() {
  const { user, setUiMode } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const choose = (m: UiMode) => {
    setError(null);
    setUiMode(m).catch(setError);
  };
  return (
    <section className="card mb-6 p-5">
      <h2 className="font-semibold text-navy-900">Mode d'utilisation</h2>
      <p className="mt-1 text-sm text-muted">Changer de mode ne modifie aucune donnée : seuls les écrans affichés changent.</p>
      <div className="mt-3"><ErrorNote error={error} /></div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {UI_MODES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => choose(m)}
            aria-pressed={user?.uiMode === m}
            className={`rounded-lg border p-3 text-left transition ${user?.uiMode === m ? 'border-navy-900 bg-tech-50' : 'border-line hover:border-tech-500'}`}
          >
            <div className="text-sm font-semibold text-navy-900">{MODE_LABELS[m]}</div>
            <div className="mt-1 text-xs text-muted">{MODE_HINTS[m]}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

function PasswordCard() {
  const { adoptSession } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '' });
  const change = useMutation({
    mutationFn: () => api<AuthResponse>('/auth/password', { method: 'POST', body: form }),
    onSuccess: (session) => {
      adoptSession(session);
      setForm({ currentPassword: '', newPassword: '' });
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    change.mutate();
  }
  return (
    <section className="card mb-6 p-5">
      <h2 className="font-semibold text-navy-900">Mon mot de passe</h2>
      <p className="mt-1 text-sm text-muted">Vos autres appareils seront déconnectés.</p>
      <form onSubmit={submit} className="mt-3 grid max-w-3xl gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Mot de passe actuel"><input className="input" type="password" autoComplete="current-password" required value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} /></Field>
        <Field label="Nouveau mot de passe"><input className="input" type="password" autoComplete="new-password" required minLength={10} value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} /></Field>
        <button className="btn-secondary" disabled={change.isPending}>Changer</button>
      </form>
      <div className="mt-2"><ErrorNote error={change.error} /></div>
      {change.isSuccess && <p className="mt-2 text-sm text-ok">Mot de passe modifié.</p>}
    </section>
  );
}

export function SettingsPage() {
  const { can } = useAuth();
  return (
    <>
      <PageHeader title="Paramètres" />
      <ModeCard />
      <PasswordCard />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Link to="/parametres/entreprise" className="card p-5 transition hover:border-tech-500">
          <Building2 className="text-brand-600" />
          <div className="mt-2 font-semibold text-navy-900">Mon entreprise</div>
          <p className="mt-1 text-sm text-muted">Coordonnées, logo, pays, devise, TVA par défaut et mentions des documents.</p>
        </Link>
        <Link to="/parametres/abonnement" className="card p-5 transition hover:border-tech-500">
          <BadgeCheck className="text-brand-600" />
          <div className="mt-2 font-semibold text-navy-900">Mon abonnement</div>
          <p className="mt-1 text-sm text-muted">Votre formule, vos limites et comment en changer.</p>
        </Link>
        {can('invoice:read') && (
          <Link to="/parametres/paiements" className="card p-5 transition hover:border-tech-500">
            <CreditCard className="text-brand-600" />
            <div className="mt-2 font-semibold text-navy-900">Moyens de paiement</div>
            <p className="mt-1 text-sm text-muted">Espèces, virement, paiements mobiles proposés à l'encaissement.</p>
          </Link>
        )}
        {can('user:read') && (
          <Link to="/parametres/utilisateurs" className="card p-5 transition hover:border-tech-500">
            <Users className="text-brand-600" />
            <div className="mt-2 font-semibold text-navy-900">Utilisateurs et rôles</div>
            <p className="mt-1 text-sm text-muted">Inviter, attribuer un rôle, désactiver un compte.</p>
          </Link>
        )}
        {can('audit:read') && (
          <Link to="/parametres/audit" className="card p-5 transition hover:border-tech-500">
            <History className="text-brand-600" />
            <div className="mt-2 font-semibold text-navy-900">Journal d'audit</div>
            <p className="mt-1 text-sm text-muted">Qui a modifié quoi, quand, et l'ancienne valeur.</p>
          </Link>
        )}
        <div className="card p-5">
          <Ruler className="text-muted" />
          <div className="mt-2 font-semibold text-navy-900">Unités, devises, TVA</div>
          <p className="mt-1 text-sm text-muted">Référentiels installés (m, m², m³, sac, barre…). La devise et la TVA se règlent dans la fiche de chaque projet.</p>
        </div>
      </div>
    </>
  );
}
